import {
  Button,
  Checkbox,
  Flex,
  Grid,
  Input,
  Stack,
  Text,
  TextLabel,
} from '@tonic-one/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ErrorAlert from '../../../../components/Common/ErrorAlert';
import { GlobalApi } from '@Apis';
import {
  PasswordExtractionRulesItem,
  TestPasswordExtractionRulesLists,
} from '../../../../types/globalSetting';
import { char2entity } from '../../../../utils/common';
import { validateSafeRegexFragments } from '../../../../utils/regex';
export const AddInput = React.memo(
  (props: {
    onChange: (params: PasswordExtractionRulesItem) => void;
    selectedExtractionRule?: PasswordExtractionRulesItem[] | [];
    isSaveDisable: (isSaveDisable: boolean) => void;
  }) => {
    const { t } = useTranslation();
    const [matchedPassword, setMatchedPassword] = useState<
      JSX.Element[] | undefined
    >(undefined);
    const [errorMsg, setErrorMsg] = React.useState<string | undefined>(
      undefined
    );
    const [params, setSelectedExtractionRule] =
      useState<TestPasswordExtractionRulesLists>({
        prefix: '',
        matchPart: '[A-Za-z0-9@#%$^&*]{2,128}',
        suffix: '',
        ignoreCase: true,
        text: '',
      });
    useEffect(() => {
      if (
        props.selectedExtractionRule &&
        props.selectedExtractionRule.length > 0
      ) {
        const selectedExtractionRules = {
          prefix: props.selectedExtractionRule[0].prefix,
          matchPart: props.selectedExtractionRule[0].matchPart,
          suffix: props.selectedExtractionRule[0].suffix,
          ignoreCase: props.selectedExtractionRule[0].ignoreCase,
          text: '',
          itemKey: props.selectedExtractionRule[0].itemKey,
        };
        setSelectedExtractionRule(selectedExtractionRules);
        console.log(selectedExtractionRules);
      }
    }, [props.selectedExtractionRule]);
    const maxPasswordRuleLength = {
      passwordRuleNum: 10,
      passwordRulePrefix: 128,
      passwordRuleMatchPart: 128,
      passwordRuleSuffix: 32,
      passwordRuleExecuteTimeout: 1000,
    };
    const passwordTestDisabled = useMemo(() => {
      return !(
        params.suffix !== '' &&
        params.text !== '' &&
        params.prefix !== '' &&
        params.matchPart !== ''
      );
    }, [params]);

    const handleChangeValue = (e: any) => {
      const _params = JSON.parse(JSON.stringify(params));
      if (e.target.name === 'ignoreCase') {
        _params[e.target.name] = !e.target.checked;
      } else {
        _params[e.target.name] = e.target.value;
      }
      setSelectedExtractionRule(_params);
      props.onChange(_params);
    };
    const onTest = async () => {
      // Validate every fragment before the backend combines them into one regex.
      const regexValidation = validateSafeRegexFragments([
        params.prefix,
        params.matchPart,
        params.suffix,
      ]);
      if (!regexValidation.isValid) {
        setErrorMsg(
          t(
            `globalSetting.AttachmentPasswordGuessing.Test.regexError.${regexValidation.error}`,
            { param1: regexValidation.maxLength }
          )
        );
        return;
      }

      const value = {
        prefix: char2entity(params.prefix),
        matchPart: char2entity(params.matchPart),
        suffix: char2entity(params.suffix),
        ignoreCase: params.ignoreCase,
        text: char2entity(params.text),
      };
      try {
        const res = await GlobalApi.testPasswordExtractionRules(value);
        if (res.data) {
          if (res.data.matchedPassword === '') {
            // window.showNotification({
            //   titleMsg: t(
            //     'globalSetting.AttachmentPasswordGuessing.Test.errorNotData'
            //   ),
            //   severity: 'error',
            // });
            setErrorMsg(
              t(`globalSetting.AttachmentPasswordGuessing.Test.errorNotData`)
            );
            setMatchedPassword(res.data.matchedPassword);
          } else {
            const matchedPassword = res.data.matchedPassword;
            const firstPassword = value.text.substr(0, res.data.matchedIndex);
            const lastPassword = value.text.substr(
              res.data.matchedIndex + res.data.matchedPassword.length
            );
            const selectedRows: any[] = [];
            const passwordList = {
              firstPassword: '',
              matchedPassword: '',
              lastPassword: '',
            };
            passwordList.firstPassword = firstPassword;
            passwordList.matchedPassword = matchedPassword;
            passwordList.lastPassword = lastPassword;
            selectedRows.push(passwordList);
            const content = selectedRows.map((item) => (
              <Flex direction="row" align="center">
                <Text key={item.matchedPassword}>
                  {char2entity(item.firstPassword)}
                </Text>
                <Text backgroundColor="#FCE79E" color="black" ml="2px">
                  {char2entity(item.matchedPassword)}
                </Text>
                <Text ml="2px">{char2entity(item.lastPassword)}</Text>
              </Flex>
            ));
            props.isSaveDisable(false);

            setMatchedPassword(content);
            // window.showNotification({
            //   titleMsg: t(
            //     'globalSetting.AttachmentPasswordGuessing.Test.success'
            //   ),
            //   severity: 'success',
            // });
          }
        }
      } catch (error: any) {
        const code = error?.status || 'Unknown';
        if (code === 400001) {
          if (error.detail === 'duplicate rule') {
            setErrorMsg(
              t(
                `globalSetting.AttachmentPasswordGuessing.Test.errorDuplicateRule`
              )
            );
          } else {
            setErrorMsg(
              t(`globalSetting.AttachmentPasswordGuessing.Test.errorInvalid`)
            );
          }
        } else if (code === 400003) {
          const _maxPasswordRuleLength = JSON.parse(
            JSON.stringify(maxPasswordRuleLength)
          );
          setErrorMsg(
            t(
              `globalSetting.AttachmentPasswordGuessing.Test.error.${error.detail}`,
              { param1: _maxPasswordRuleLength[error.detail] }
            )
          );
        } else {
          setErrorMsg(t(`common.apiException.internalError`));
        }
      }
    };
    return (
      <>
        {errorMsg && (
          <ErrorAlert
            errorMsg={errorMsg}
            onClose={() => setErrorMsg(undefined)}
          />
        )}
        <Stack direction="column" spacing="4x">
          <Text size="md" mb="15px" fontWeight="semibold">
            {t('globalSetting.AttachmentPasswordGuessing.Expression.title')}:
          </Text>
        </Stack>
        <Grid
          ml="10px"
          templateColumns="min-content auto"
          columnGap="3x"
          rowGap="3x"
        >
          <Grid>
            <TextLabel width={120} display="inline-flex" alignItems="center">
              {t(
                'globalSetting.AttachmentPasswordGuessing.column.precedingText'
              )}
              :
            </TextLabel>
          </Grid>
          <Grid>
            <Input
              onChange={handleChangeValue}
              value={params.prefix}
              maxLength={maxPasswordRuleLength.passwordRulePrefix}
              name="prefix"
              placeholder={t(
                'globalSetting.AttachmentPasswordGuessing.ExtractionRule.precedingText'
              )}
            />
          </Grid>
          <Grid>
            <TextLabel width={120} display="inline-flex" alignItems="center">
              {t('globalSetting.AttachmentPasswordGuessing.column.password')}:
            </TextLabel>
          </Grid>
          <Grid>
            <Input
              onChange={handleChangeValue}
              value={params.matchPart}
              name="matchPart"
              maxLength={maxPasswordRuleLength.passwordRuleMatchPart}
              placeholder="[A-Za-z0-9@#%$^&*]{2,128}"
            />
          </Grid>
          <Grid>
            <TextLabel width={120} display="inline-flex" alignItems="center">
              {t(
                'globalSetting.AttachmentPasswordGuessing.column.subsequentText'
              )}
              :
            </TextLabel>
          </Grid>
          <Grid>
            <Input
              onChange={handleChangeValue}
              value={params.suffix}
              maxLength={maxPasswordRuleLength.passwordRuleSuffix}
              name="suffix"
              placeholder={t(
                'globalSetting.AttachmentPasswordGuessing.ExtractionRule.subsequentText'
              )}
            />
          </Grid>
          <Grid>
            <Checkbox
              checked={!params.ignoreCase}
              onChange={handleChangeValue}
              name="ignoreCase"
            >
              {t('globalSetting.AttachmentPasswordGuessing.ignoreCase.title')}
            </Checkbox>
          </Grid>
        </Grid>
        <Stack direction="column" spacing="4x" mt="15px">
          <Text size="md" mb="10px" fontWeight="semibold">
            {t('globalSetting.AttachmentPasswordGuessing.Test.title')}:
          </Text>
        </Stack>
        <Grid
          ml="10px"
          columnGap="3x"
          rowGap="3x"
          templateColumns="120px 1.5fr 5rem"
          gap="3x"
          mt="0px"
        >
          <Grid>
            <TextLabel width={120} display="inline-flex" alignItems="center">
              {t('globalSetting.AttachmentPasswordGuessing.Test.testData')}:
            </TextLabel>
          </Grid>
          <Grid>
            <Input
              onChange={handleChangeValue}
              name="text"
              value={params.text}
              placeholder={t(
                'globalSetting.AttachmentPasswordGuessing.ExtractionRule.testData'
              )}
            />
          </Grid>
          <Grid>
            <Button
              display="inline-flex"
              width={20}
              variant="primary"
              minWidth="20x"
              disabled={passwordTestDisabled}
              onClick={onTest}
            >
              {t('globalSetting.AttachmentPasswordGuessing.Test.title')}
            </Button>
          </Grid>
        </Grid>
        <Grid
          ml="10px"
          templateColumns="min-content auto"
          columnGap="3x"
          rowGap="3x"
          mt="15px"
        >
          <Grid>
            <TextLabel width={120} display="inline-flex" alignItems="center">
              {t('globalSetting.AttachmentPasswordGuessing.Test.testResult')}
            </TextLabel>
          </Grid>
          <Grid>
            <Text display="inline-flex" alignItems="center">
              {matchedPassword}
            </Text>
          </Grid>
        </Grid>
      </>
    );
  }
);
